import type { ReactNode } from "react";

import { cx } from "@/lib/cx";

/**
 * Icons used only by the inbox.
 *
 * Same 20×20 grid and 1.6px round-capped stroke as `ui/overlay/icons`, because
 * these sit in the same contextual menus as the shared bin and pencil — see the
 * note in `features/tasks/components/icons.tsx`, which this follows exactly.
 *
 * Anything a second surface would also want (the bin, the archive box, the
 * clipboard, the download arrow) already lives in `@/ui` and is imported from
 * there rather than redrawn here. What is left is genuinely only ours: the two
 * envelope states, the key, and the paperclip.
 */

type IconProps = { className?: string };

function Stroke({ className, children }: IconProps & { children: ReactNode }) {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className={cx("size-4 shrink-0", className)}
    >
      {children}
    </svg>
  );
}

/** Unread — a sealed envelope. Marks the "back to new" action. */
export function EnvelopeIcon({ className }: IconProps) {
  return (
    <Stroke className={className}>
      <rect x="2.5" y="4.5" width="15" height="11" rx="2" />
      <path d="m2.5 6.5 6.6 4.4a1.6 1.6 0 0 0 1.8 0L17.5 6.5" />
    </Stroke>
  );
}

/** Read — the same envelope, opened. */
export function EnvelopeOpenIcon({ className }: IconProps) {
  return (
    <Stroke className={className}>
      <path d="M2.5 8.75 10 3.5l7.5 5.25V15a1.5 1.5 0 0 1-1.5 1.5H4a1.5 1.5 0 0 1-1.5-1.5z" />
      <path d="m2.5 8.75 6.6 4.4a1.6 1.6 0 0 0 1.8 0l6.6-4.4" />
    </Stroke>
  );
}

/** The shared token — a key. Opens the API panel. */
export function KeyIcon({ className }: IconProps) {
  return (
    <Stroke className={className}>
      <circle cx="6.75" cy="10" r="3.25" />
      <path d="M10 10h7" />
      <path d="M14.25 10v2.75" />
      <path d="M16.75 10v2" />
    </Stroke>
  );
}

/** Attachments, on the row's trailing edge. */
export function PaperclipIcon({ className }: IconProps) {
  return (
    <Stroke className={className}>
      <path d="M14.5 9.25 9.4 14.4a3.1 3.1 0 0 1-4.4-4.4l5.9-5.9a2.1 2.1 0 0 1 3 3l-5.9 5.9a1.1 1.1 0 0 1-1.5-1.5l5.2-5.2" />
    </Stroke>
  );
}

/** The empty state: an open tray with nothing in it. */
export function EmptyTrayIcon({ className }: IconProps) {
  return (
    <Stroke className={cx("size-6", className)}>
      <path d="M2.75 11.5 5 4.5h10l2.25 7" />
      <path d="M2.75 11.5h3.5l1 2h5.5l1-2h3.5v3.5a1.5 1.5 0 0 1-1.5 1.5H4.25a1.5 1.5 0 0 1-1.5-1.5z" />
    </Stroke>
  );
}
